import Page from 'components/Layout/Page'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Balance, Box, CloseIcon, Flex, FlexGap, SearchIcon, Text, TokenLogo, useMatchBreakpoints, } from '@pancakeswap/uikit'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useSWRImmutable from 'swr/immutable'
import { statsClients, v3Clients } from 'utils/graphql'
import { ChainDefaultId } from '@pancakeswap/sdk'
import { gql } from 'graphql-request'
import { useCurrency } from 'hooks/Tokens'
import { DoubleCurrencyLogo } from 'components/Logo'
import { useCurrentBlock } from 'state/block/hooks'
import { getAddress } from '@ethersproject/address'
import { REWARD_RATIO } from 'config/constants'

const PageStyled = styled(Page)`
    padding: 20px;
    color: #fff;
    background: transparent;
    max-width: 800px;

    @media (max-width: 800px) {
        padding: 0;
        max-width: 100%;
    }
`

const Section = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 32px;
  text-align: center;
`

const LeaderBoardSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 32px;
`

const SubSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
`

const Description = styled.p`
  font-size: 16px;
  margin-bottom: 16px;
  color: white;
  font-weight: bold;
`

const PairGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const PairItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 12px;
  padding: 8px;
  color: #fff;
`

const TableWrapper = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  padding: 16px;
  text-align: left;
  
  @media (max-width: 800px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`

const Td = styled.td`
  padding: 16px;
  
  @media (max-width: 800px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`

const SearchBox = styled(Flex)`
	flex: 1;
	border: 1px solid #FFFFFF33;
	border-radius: 12px;
	padding: 8px 17px;
	align-items: center;
	svg {
		opacity: 0.3;
	}
	& > input {
		background: transparent;
		border: none;
		outline: 0;
		flex: 1;
		font-size: 14px;
		&::placeholder {
			color: #958BB0;
		}
	}
`

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
const StyledLogo = styled(TokenLogo) <{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border-radius: 50%;
`

interface Farm {
    token0: {
        address: string
        symbol: string
    }
    token1: {
        address: string
        symbol: string
    }
}

interface User {
    ranking: number
    address: string
    points: number
    amount: number
    block: number
}

const FarmItem = ({ farm }: { farm: Farm }) => {
    const currency0 = useCurrency(farm.token0.address)
    const currency1 = useCurrency(farm.token1.address)
    const { isMobile } = useMatchBreakpoints()

    return <FlexGap gap="8px" flex={1}>
        <DoubleCurrencyLogo currency0={currency0} currency1={currency1} />
        <Text fontSize={isMobile ? 12 : 16}>{currency0?.symbol} - {currency1?.symbol}</Text>
    </FlexGap>
}

const Rewards = () => {
    const [searchQuery, setSearchQuery] = useState("")
    const { account } = useActiveWeb3React()

    const blockNumber = useCurrentBlock()

    const { isMobile } = useMatchBreakpoints()

    const { data: farms } = useSWRImmutable(
        [`reward/farms`],
        async () => {
            const data = await v3Clients[ChainDefaultId].request<{ farms: Farm[] }>(gql`
                query {
                    farms(where: { enabled: true }) {
                        token0 {
                            address:id symbol
                        }
                        token1 {
                            address:id symbol
                        }
                    }
                }
            `)
            return data.farms
        },
        {
            refreshInterval: 600_000,
            keepPreviousData: false,
        }
    )

    const { data: users } = useSWRImmutable(
        `reward/ranking`,
        async () => {
            const result = await statsClients[ChainDefaultId].request<{ users: any[] }>(gql`
                query {
                    users:points (first: 25, orderBy: ranking, orderDirection: asc) {
                        id points amount ranking blockNumber
                    }
                }
            `)
            return result.users.map(user => ({
                address: user.id,
                ranking: Number(user.ranking),
                points: Number(user.points),
                amount: Number(user.amount),
                block: Number(user.blockNumber),
            } as User))
        },
        {
            refreshInterval: 300_000,
            keepPreviousData: false,
        }
    )

    const { data: me } = useSWRImmutable(['reward/me', account])

    const getRankIcon = (rank: number) => {
        let icon = ''
        if (rank === 1) {
            icon = '🥇'
        } else if (rank === 2) {
            icon = '🥈'
        } else if (rank === 3) {
            icon = '🥉'
        } else {
            icon = `${rank}`
        }
        return icon
    }

    const abbreviateAddress = (address: string) => {
        const checksumAddress = getAddress(address)
        return `${checksumAddress.slice(0, 7)}...${checksumAddress.slice(-5)}`
    }

    const sortedUsers = useMemo(() => {
        return users?.map(user => ({
            ...user,
            points: (user.points + (blockNumber - user.block) * user.amount) * REWARD_RATIO
        })) ?? []
    }, [users, blockNumber])

    const isTestingUser = ["0x1559141e6075a04fd02f42be208479ced37ac702", "0xed6781e11893f334eae70c55f64c701a296dbec3"].includes(account?.toLowerCase() || "")

    return (
        <PageStyled>

            {isTestingUser ? (
                <>
                    <Section>
                        {
                            account
                                ? <>
                                    <Text fontSize={20} mt={2} bold>Your Points</Text>
                                    <Balance bold
                                        color="white"
                                        fontSize={64}
                                        mb="16px"
                                        decimals={4}
                                        value={me ? Number(me.points + (blockNumber - me.block) * me.amount) * REWARD_RATIO : 0}
                                    />
                                </>
                                : <Box mt={5} />
                        }
                        <Description>
                            Points represent the amount of tokens you will be credited on the launch of the DEX's token
                        </Description>
                        <Description>
                            100% of supply is distributed to liquidity providers
                        </Description>
                        <Description>
                            Liquidity providers on pDEX earn Points when they stake their LP tokens
                        </Description>
                        <Description>
                            Staked LP tokens still earn fees, and can be unstaked any time with no penalty
                        </Description>
                        <br />
                        <br />

                        <SubSection>
                            <Text fontWeight="bold" fontSize="15px" mb="12px">The following LP pairs can be staked and earn Points:</Text>
                            <SearchBox mb="12px">
                                <SearchIcon width={24} />
                                <input
                                    placeholder="Search by name, symbol or address"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value)
                                    }}
                                />
                                {
                                    searchQuery?.length > 0 &&
                                    <CloseIcon width={24} onClick={() => {
                                        setSearchQuery('')
                                    }} />
                                }
                            </SearchBox>
                            <PairGrid>
                                {
                                    farms?.filter(farm => {
                                        if (!searchQuery) return true;
                                        const query = searchQuery.toLowerCase();
                                        return farm.token0.symbol.toLowerCase().includes(query) ||
                                            farm.token1.symbol.toLowerCase().includes(query);
                                    }).map((farm, idx) => (
                                        <FarmItem key={idx} farm={farm} />
                                    ))}
                            </PairGrid>
                        </SubSection>
                    </Section>
                    {
                        !!users &&
                        <LeaderBoardSection>
                            <Text fontWeight="bold" fontSize="24px" mb="12px">Top Earners</Text>
                            <TableWrapper>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>#</Th>
                                            <Th>Address</Th>
                                            <Th>Points</Th>
                                            <Th>Total LP</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            sortedUsers.map((user, idx) => (
                                                <tr key={idx + 1}>
                                                    <Td style={{
                                                        fontSize: isMobile ? "12px" :
                                                            (getRankIcon(idx + 1) === "🥇" ||
                                                                getRankIcon(idx + 1) === "🥈" ||
                                                                getRankIcon(idx + 1) === "🥉"
                                                                ? "25px" : "16px")
                                                    }}>{getRankIcon(idx + 1)}</Td>
                                                    <Td>{abbreviateAddress(user.address)}</Td>
                                                    <Td>
                                                        <Balance bold
                                                            color="white"
                                                            fontSize={isMobile ? 12 : 16}
                                                            mb={isMobile ? "8px" : "16px"}
                                                            decimals={0}
                                                            value={user.points}
                                                        />
                                                    </Td>
                                                    <Td>${Number(user.amount).toFixed(2)}</Td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </Table>
                            </TableWrapper>
                        </LeaderBoardSection>
                    }

                </>
            ) : (

                <Flex
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    height="100%"
                    width="100%"
                >
                    <p style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                    }}>
                        Points Page Under Maintenance
                    </p>
                    <br />
                    <br />
                    <p>
                        We're currently making some improvements to the Points system to ensure everything runs smoothly and fairly for everyone.
                    </p>
                    <br />
                    <p>
                        <span style={{ fontWeight: "bold" }}>All earned points will be reset</span> as part of this update, this is to ensure a clean and fair restart for everyone.
                    </p>
                    <br />
                    <p>
                        Thanks for your patience, we’ll be back shortly!
                    </p>
                </Flex>
            )}





        </PageStyled>
    )
}

export default Rewards